import { Button, Fieldset, FormSection, Layout } from '@vwfs-bronson/bronson-react';
import { Form, Formik, useFormikContext } from 'formik';
import { t } from 'i18next';
import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { ObjectSchema } from 'yup';
import { FormHelpers, OnFormInitialValuesChange } from '../../../../../screens/utils';
import {
  getNaceCodesOptionsListBySection,
  naceCodesOptionsList,
  naceSectionsOptionsList,
} from '../../../../../services/common/naceCodes';
import { journeyDataActions } from '../../../../../services/redux/features/journeyData.slice';
import { FormSectionEditButton } from '../../../../FormSectionEditButton';
import { FormSectionSubmitButton } from '../../../../FormSectionSubmitButton';
import { RenderFormSectionSummaryTitle } from '../../../../FormSectionSummaryTitle';
import { CompanyDetailsFormModel_OB_FP } from './formModel';
import { getInitialValues } from './initialValues';
import { requestMapper } from './requestMapper';
import { getValidationSchema } from './validationSchema';
import { formAutocomplete } from '../../../../../config';
import { createFormInputs } from '../../../../ApplicationFormInputs/applicationFormInputs';
import { useFlowTypeChecker } from '../../../../../services/common/miscUtils';
import {
  checkIfEnterpriseSizeIsOptional,
  maxLandlineNumberLength,
  maxMobileNumberLength,
} from '../../../../../services/common/validation';
import { TelephoneTypeEnum } from '../../../../../types/forms';

type Props = {
  formHelpers: FormHelpers;
};

/**
 * - OtherBusiness (LTD, PLC, Other, Unlimited, Partnership)
 * - FinancialProduct
 */
export const CompanyDetailsForm_OB_FP: React.FC<Props> = ({ formHelpers }) => {
  const [initialValues] = useState(getInitialValues);
  const [validationSchema] = useState<ObjectSchema<Partial<CompanyDetailsFormModel_OB_FP>>>(getValidationSchema);

  const {
    formSectionProps,
    onFormSubmitSuccess,
    onFormInitialValuesChange,
    onStartEditInSummaryMode,
    isDisabledFormSummary,
  } = formHelpers;

  const onSubmit = (/* values: ThisFormModel */) => {
    /* start loader */
    Promise.resolve(/* submit */).then(onFormSubmitSuccess).catch(/* on error */).finally(/* end loader */);
  };

  return (
    <FormSection
      title={RenderFormSectionSummaryTitle(t(`companyDetailsForm_OL:title:business`), isDisabledFormSummary)}
      {...formSectionProps}
    >
      <FormSectionEditButton onClick={onStartEditInSummaryMode} formHelpers={formHelpers} />
      <Formik validationSchema={validationSchema} initialValues={initialValues} onSubmit={onSubmit} enableReinitialize>
        {/* eslint-disable-next-line no-use-before-define */}
        <FormContent onFormInitialValuesChange={onFormInitialValuesChange} formHelpers={formHelpers} />
      </Formik>
    </FormSection>
  );
};

type FormContentProps = {
  formHelpers: FormHelpers;
  onFormInitialValuesChange: OnFormInitialValuesChange;
};

export const FormContent = ({ formHelpers }: FormContentProps) => {
  const [inputs] = useState(() => createFormInputs('companyDetailsForm_OL'));
  const dispatch = useDispatch();
  const { values, submitForm, setFieldValue } = useFormikContext<CompanyDetailsFormModel_OB_FP>();
  const { isDisabledFormSummary } = formHelpers;

  useEffect(() => {
    dispatch(journeyDataActions.updateData(requestMapper(values)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values]);

  const [businessSubTypeOptions, setBusinessSubTypeOptions] = useState(() =>
    values.businessType ? getNaceCodesOptionsListBySection(values.businessType) : naceCodesOptionsList
  );

  // Reset business sub type if business type changed, and sub type is no longer valid.
  useEffect(() => {
    const newBusinessSubTypeOptions = values.businessType
      ? getNaceCodesOptionsListBySection(values.businessType)
      : naceCodesOptionsList;

    setBusinessSubTypeOptions(newBusinessSubTypeOptions);

    if (newBusinessSubTypeOptions.some(({ value }) => value === values.businessSubType)) return;
    setFieldValue('businessSubType', undefined);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.businessType]);

  const disableForm = isDisabledFormSummary;

  const flowTypeIs = useFlowTypeChecker();

  const enterpriseSizeOptional = checkIfEnterpriseSizeIsOptional(values.institutionalType);

  const selectedMobileTelephoneType = values.telephoneType === TelephoneTypeEnum.mobile;
  const selectedLandlineTelephoneType = values.telephoneType === TelephoneTypeEnum.landline;

  // reset phone number when phone type changed.
  useEffect(() => {
    if (selectedMobileTelephoneType) {
      setFieldValue('phoneNumber', values.phoneNumber?.slice(0, maxMobileNumberLength));
    } else if (selectedLandlineTelephoneType) {
      setFieldValue('phoneNumber', values.phoneNumber?.slice(0, maxLandlineNumberLength));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.telephoneType]);

  /* Disable prettier to keep template compact and readable. */
  /* prettier-ignore */
  return (
    <Form autoComplete={formAutocomplete}>
      <Fieldset disabled={disableForm}>
        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.croNumber()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.croNumberButton(!values.croNumber)}</Layout.Item>     
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.identityType()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">{inputs.identityNumber()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/2" s="1/1">{inputs.registeredPartnershipName()}</Layout.Item>
            <Layout.Item default="1/2" s="1/1">
              <br /> {/* Necessary spacing */}
              <Layout>
                <Layout.Item default="1/2">
                  <Button type="button" full secondary icon="address-book" ariaLabel="Search existing customer" onClick={() => ''}>
                    {t('companyDetailsForm:searchExistingCustomer')}
                  </Button>
                </Layout.Item>
                <Layout.Item default="1/2">
                  <Button type="button" full secondary icon="address-book" ariaLabel="Search CRO" onClick={() => ''}>
                    {t('companyDetailsForm:searchCro')}
                  </Button>
                </Layout.Item>
              </Layout>
            </Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3" s="1/1">{inputs.businessType(naceSectionsOptionsList)}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{inputs.businessSubType(businessSubTypeOptions)}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{inputs.institutionalType()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3" s="1/1">{inputs.entityForm()}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{inputs.dateOfIncorporation()}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{inputs.enterpriseSize(enterpriseSizeOptional)}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        {flowTypeIs('partnership') && (
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="1/3" s="1/1">{inputs.vatNumber()}</Layout.Item>
              <Layout.Item default="1/3" s="1/1">{inputs.annualProfitCurrentYear()}</Layout.Item>
              <Layout.Item default="1/3" s="1/1">{inputs.annualProfitPreviousYear()}</Layout.Item>
            </Layout>
          </Fieldset.Row>
        )}

        {flowTypeIs('ltd', 'plc') && (
          <>
            <Fieldset.Row>
              <Layout>
                <Layout.Item default="1/3" s="1/1">{inputs.vatNumber()}</Layout.Item>
              </Layout>
            </Fieldset.Row>
            <Fieldset.Row>
              <Layout>
                <Layout.Item default="1/3" s="1/1">{inputs.annualProfitCurrentYear()}</Layout.Item>
                <Layout.Item default="1/3" s="1/1">{inputs.annualProfitPreviousYear()}</Layout.Item>
              </Layout>
            </Fieldset.Row>
          </>
        )}

        {flowTypeIs('partnership', 'ltd', 'plc') && (
          <Fieldset.Row>
            <Layout>
              <Layout.Item default="1/2" s="1/1">{inputs.businessHistory()}</Layout.Item>
              <Layout.Item default="1/2" s="1/1">{inputs.currentBusinessContracts()}</Layout.Item>
            </Layout>
          </Fieldset.Row>
        )}

        {flowTypeIs('other') && (
          <>
            <Fieldset.Row>
              <Layout>
                <Layout.Item default="1/3" s="1/1">{inputs.vatNumber()}</Layout.Item>
              </Layout>
            </Fieldset.Row>
            <Fieldset.Row>
              <Layout>
                <Layout.Item default="1/2" s="1/1">{inputs.businessHistory()}</Layout.Item>
                <Layout.Item default="1/2" s="1/1">{inputs.currentBusinessContracts()}</Layout.Item>
              </Layout>
            </Fieldset.Row>
          </>
        )}

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3" s="1/1">{inputs.telephoneType()}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{inputs.countryCode()}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{inputs.phoneNumber(selectedMobileTelephoneType)}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3" s="1/1">{inputs.correspondenceMethod()}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{/* Empty */}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{inputs.email()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3" s="1/1">{inputs.contactFirstName()}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{inputs.contactLastName()}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{inputs.contactPosition()}</Layout.Item>
          </Layout>
        </Fieldset.Row>

        <Fieldset.Row>
          <Layout>
            <Layout.Item default="1/3" s="1/1">{inputs.contactCountryCode()}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{/* Empty */}</Layout.Item>
            <Layout.Item default="1/3" s="1/1">{inputs.contactPhoneNumber()}</Layout.Item>
          </Layout>
        </Fieldset.Row>
      </Fieldset>

      <FormSectionSubmitButton onClick={submitForm} formHelpers={formHelpers}/>
    </Form>
  );
};
